import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";
import { getMenuItems } from "../permissions";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userName, setUserName] = useState("Admin");
  const [userType, setUserType] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const session = localStorage.getItem("user_session");
    if (!session) {
      navigate("/");
      return;
    }
    try {
      const userData = JSON.parse(session);
      if (userData && userData.name) setUserName(userData.name);
      if (userData && userData.type) setUserType(userData.type);
    } catch (e) {
      localStorage.removeItem("user_session");
      navigate("/");
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("user_session");
    navigate("/");
  };

  // Role-based menu items - filters based on user type
  const menuItems = getMenuItems(userType);

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <style>
        {`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        body {
          font-family: 'Inter', 'Segoe UI', sans-serif;
          background: #f1f5f9;
          color: #1e293b;
        }

        /* ===== LAYOUT ===== */
        .db-layout {
          display: flex;
          height: 100vh;
          overflow: hidden;
        }

        /* ===== SIDEBAR ===== */
        .db-sidebar {
          width: 270px;
          background: #ffffff;
          border-right: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          z-index: 100;
          flex-shrink: 0;
        }

        .db-sidebar.collapsed {
          width: 0;
          overflow: hidden;
          border: none;
        }

        .db-sidebar-brand {
          padding: 22px 20px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #f1f5f9;
        }

        .db-sidebar-brand-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .db-sidebar-logo {
          width: 36px;
          height: 36px;
          object-fit: contain;
          border-radius: 8px;
        }

        .db-sidebar-title {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.3px;
        }

        .db-sidebar-subtitle {
          font-size: 11px;
          color: #94a3b8;
          font-weight: 500;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .db-sidebar-close {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #64748b;
          cursor: pointer;
          font-size: 14px;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .db-sidebar-close:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        /* Menu */
        .db-menu-section {
          padding: 16px 12px;
          flex: 1;
          overflow-y: auto;
        }

        .db-menu-label {
          font-size: 10px;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 1.2px;
          padding: 0 12px;
          margin-bottom: 10px;
        }

        .db-menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 14px;
          margin-bottom: 3px;
          border-radius: 10px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
          transition: all 0.2s ease;
          white-space: nowrap;
          position: relative;
        }

        .db-menu-item:hover {
          background: #f8fafc;
          color: #334155;
        }

        .db-menu-item.active {
          background: linear-gradient(135deg, #eff6ff, #eef2ff);
          color: #2563eb;
          font-weight: 600;
        }

        .db-menu-item.active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 20px;
          background: #2563eb;
          border-radius: 0 4px 4px 0;
        }

        .db-menu-icon {
          font-size: 17px;
          width: 24px;
          text-align: center;
        }

        /* Sidebar Footer */
        .db-sidebar-footer {
          padding: 14px;
          border-top: 1px solid #f1f5f9;
        }

        .db-user-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 12px;
          margin-bottom: 10px;
        }

        .db-user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, #3b82f6, #6366f1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          flex-shrink: 0;
        }

        .db-user-info {
          overflow: hidden;
        }

        .db-user-name {
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .db-user-role {
          font-size: 11px;
          color: #94a3b8;
          text-transform: capitalize;
        }

        .db-logout-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 10px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 10px;
          color: #dc2626;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Inter', sans-serif;
        }

        .db-logout-btn:hover {
          background: #fee2e2;
          border-color: #fca5a5;
        }

        /* ===== MAIN AREA ===== */
        .db-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          min-width: 0;
        }

        /* Header */
        .db-header {
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
          padding: 0 24px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }

        .db-header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .db-hamburger {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #64748b;
          cursor: pointer;
          font-size: 20px;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .db-hamburger:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        .db-search-box {
          position: relative;
        }

        .db-search-box input {
          width: 300px;
          padding: 10px 14px 10px 40px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #1e293b;
          font-size: 13px;
          font-family: 'Inter', sans-serif;
          outline: none;
          transition: all 0.2s;
        }

        .db-search-box input::placeholder {
          color: #94a3b8;
        }

        .db-search-box input:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          background: #fff;
        }

        .db-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 14px;
          color: #94a3b8;
          pointer-events: none;
        }

        .db-header-right {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .db-notif-btn {
          position: relative;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #64748b;
          cursor: pointer;
          font-size: 18px;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .db-notif-btn:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        .db-notif-dot {
          position: absolute;
          top: 8px;
          right: 9px;
          width: 8px;
          height: 8px;
          background: #ef4444;
          border-radius: 50%;
          border: 2px solid #fff;
        }

        .db-header-profile {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 12px 6px 6px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .db-header-profile:hover {
          background: #f1f5f9;
        }

        .db-header-avatar {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          background: linear-gradient(135deg, #3b82f6, #6366f1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 700;
          color: #fff;
        }

        .db-header-name {
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
        }

        .db-header-role {
          font-size: 11px;
          color: #94a3b8;
          text-transform: capitalize;
        }

        /* Content */
        .db-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          background: #f1f5f9;
        }

        .db-content::-webkit-scrollbar {
          width: 6px;
        }

        .db-content::-webkit-scrollbar-track {
          background: transparent;
        }

        .db-content::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        /* ===== MOBILE OVERLAY ===== */
        .db-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.3);
          z-index: 99;
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 1024px) {
          .db-search-box input {
            width: 200px;
          }
        }

        @media (max-width: 768px) {
          .db-sidebar {
            position: fixed;
            left: 0;
            top: 0;
            height: 100vh;
            z-index: 100;
            box-shadow: 4px 0 20px rgba(0,0,0,0.1);
          }

          .db-sidebar.collapsed {
            width: 0;
          }

          .db-overlay.show {
            display: block;
          }

          .db-search-box {
            display: none;
          }

          .db-header-name,
          .db-header-role {
            display: none;
          }

          .db-content {
            padding: 16px;
          }
        }

        @media (max-width: 480px) {
          .db-header {
            padding: 0 14px;
          }

          .db-content {
            padding: 12px;
          }
        }
        `}
      </style>

      {/* Mobile overlay */}
      <div
        className={`db-overlay ${sidebarOpen && window.innerWidth <= 768 ? "show" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      <div className="db-layout">
        {/* ===== SIDEBAR ===== */}
        <aside className={`db-sidebar ${sidebarOpen ? "" : "collapsed"}`}>
          {/* Brand */}
          <div className="db-sidebar-brand">
            <div className="db-sidebar-brand-left">
              <img src={logo} alt="SBRE" className="db-sidebar-logo" />
              <div>
                <div className="db-sidebar-title">SBRE</div>
                <div className="db-sidebar-subtitle">Real Estate</div>
              </div>
            </div>
            <button className="db-sidebar-close" onClick={() => setSidebarOpen(false)}>
              ✕
            </button>
          </div>

          {/* Menu */}
          <div className="db-menu-section">
            <div className="db-menu-label">Main Menu</div>
            {menuItems.filter(item => ["dashboard", "bookings", "reports", "payments", "unconfirm_payments", "cheques", "demands"].includes(item.feature)).map((item) => (
              <div
                key={item.path}
                className={`db-menu-item ${isActive(item.path) ? "active" : ""}`}
                onClick={() => {
                  navigate(item.path);
                  if (window.innerWidth <= 768) setSidebarOpen(false);
                }}
              >
                <span className="db-menu-icon">{item.icon}</span>
                {item.label}
              </div>
            ))}

            {menuItems.filter(item => ["properties", "units", "customers", "registry", "settings"].includes(item.feature)).length > 0 && (
              <div className="db-menu-label" style={{ marginTop: 20 }}>Management</div>
            )}
            {menuItems.filter(item => ["properties", "units", "customers", "registry", "settings"].includes(item.feature)).map((item) => (
              <div
                key={item.path}
                className={`db-menu-item ${isActive(item.path) ? "active" : ""}`}
                onClick={() => {
                  navigate(item.path);
                  if (window.innerWidth <= 768) setSidebarOpen(false);
                }}
              >
                <span className="db-menu-icon">{item.icon}</span>
                {item.label}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="db-sidebar-footer">
            <div className="db-user-card">
              <div className="db-user-avatar">
                {userName.charAt(0).toUpperCase()}
              </div>
              <div className="db-user-info">
                <div className="db-user-name">{userName}</div>
                <div className="db-user-role">{userType || "Admin"}</div>
              </div>
            </div>
            <button className="db-logout-btn" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </aside>

        {/* ===== MAIN ===== */}
        <div className="db-main">
          {/* Header */}
          <header className="db-header">
            <div className="db-header-left">
              {!sidebarOpen && (
                <button className="db-hamburger" onClick={() => setSidebarOpen(true)}>
                  ☰
                </button>
              )}
              <div className="db-search-box">
                <span className="db-search-icon">🔍</span>
                <input type="text" placeholder="Search properties, customers..." />
              </div>
            </div>

            <div className="db-header-right">
              <button className="db-notif-btn">
                🔔
                <span className="db-notif-dot" />
              </button>
              <div className="db-header-profile" onClick={() => navigate("/dashboard/settings")}>
                <div className="db-header-avatar">
                  {userName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="db-header-name">{userName}</div>
                  <div className="db-header-role">{userType || "Admin"}</div>
                </div>
              </div>
            </div>
          </header>

          {/* Content Outlet */}
          <main className="db-content">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
};

export default DashboardLayout;
